/**
 * HomeDesktopEditorial — تخطيط الصفحة الرئيسيّة على الديسكتوب فقط (md: فأعلى).
 *
 * الطابع: تحريريّ ناعم — هيرو بعرض الشاشة، بيان مركزيّ بخطّ serif،
 * بلوك منتج نصفين، شريط صور، ثمّ شبكة العطور.
 * النصوص من لوحة `/admin/home-desktop` وإلّا من i18n، والوسائط من الأدمن أو Shopify.
 */
import * as React from "react";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useLocale } from "@/contexts/LocaleContext";
import { useProductTranslations } from "@/hooks/useProductTranslations";
import { useHomeDesktopConfig } from "@/hooks/useHomeDesktopConfig";
import { useIsMobile } from "@/hooks/use-mobile";
import type { HomeDesktopConfig } from "@/lib/home-desktop-config";
import {
  fetchProducts,
  shopifyImg,
  shopifySrcSet,
  type ShopifyProduct,
} from "@/lib/shopify";
import { localizedTitle, localizedDescription } from "@/lib/shopify-localize";
import { AutoMedia } from "@/components/AutoMedia";
import outroVideo from "@/assets/home-outro.mp4.asset.json";

/* ─────────────────────── بلوك الفيديو السفليّ (ديسكتوب) ─────────────────────── */

function OutroVideo({ cfg }: { cfg: HomeDesktopConfig["outro"] }) {
  const { t, locale } = useLocale();
  const ar = locale === "ar";
  const caption = (ar ? cfg.captionAr : cfg.captionEn).trim() || t("home.d.outro.caption");
  const holderRef = React.useRef<HTMLDivElement | null>(null);
  // الفيديو 2.7MB: لا يُطلب أبداً على الموبايل (بوّابة JS لا CSS)،
  // وعلى الديسكتوب يُركَّب فقط عند الاقتراب من القسم.
  const [mountVideo, setMountVideo] = React.useState(false);

  React.useEffect(() => {
    if (typeof window === "undefined") return;
    if (!window.matchMedia("(min-width: 768px)").matches) return;
    const el = holderRef.current;
    if (!el) return;
    if (!("IntersectionObserver" in window)) { setMountVideo(true); return; }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setMountVideo(true);
          io.disconnect();
        }
      },
      { rootMargin: "400px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <section className="hidden md:block">
      <div ref={holderRef} className="relative h-[clamp(340px,38vw,620px)] w-full overflow-hidden bg-black">
        {mountVideo ? (
          <video
            src={outroVideo.url}
            autoPlay
            muted
            loop
            playsInline
            preload="none"
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : null}
      </div>
      {caption ? (
        <div className="bg-foreground px-8 py-[clamp(72px,8vw,132px)] text-center">
          <p
            className="mx-auto max-w-4xl font-serif font-normal leading-[1.3] tracking-[-0.012em] text-[clamp(22px,2.2vw,34px)] text-background"
            style={{ textWrap: "balance" }}
          >
            {caption}
          </p>
          {cfg.href ? (
            <Link
              to={cfg.href}
              className="mt-6 inline-flex min-h-[44px] items-center border-b border-background/40 pb-0.5 text-[11px] uppercase tracking-[0.2em] text-background/80 transition-colors hover:text-background"
            >
              {t("home.d.fragrances.all")}
            </Link>

          ) : null}
        </div>
      ) : null}
    </section>
  );
}

/* ───────────────────────────── البيان المركزيّ ───────────────────────────── */

function Statement({ cfg }: { cfg: HomeDesktopConfig["statement"] }) {
  const { t, locale } = useLocale();
  const ar = locale === "ar";
  const pick = (a: string, e: string, key: string) =>
    (ar ? a : e).trim() || t(key);

  const line1 = pick(cfg.line1Ar, cfg.line1En, "home.d.statement.line1");
  const line2 = pick(cfg.line2Ar, cfg.line2En, "home.d.statement.line2");
  const body = pick(cfg.bodyAr, cfg.bodyEn, "home.d.statement.body");
  const cta = pick(cfg.ctaLabelAr, cfg.ctaLabelEn, "home.d.statement.cta");

  return (
    <section className="hidden bg-background md:block">
      {cfg.mediaUrl ? (
        <div className="relative h-[clamp(300px,32vw,520px)] w-full overflow-hidden bg-secondary/40">
          <AutoMedia
            src={cfg.mediaUrl}
            alt={line1}
            className="absolute inset-0 h-full w-full object-cover"
          />
        </div>
      ) : null}
      <div className="mx-auto max-w-[900px] px-10 py-[clamp(80px,9vw,150px)] text-center">
        <h2
          className="font-serif font-normal leading-[1.14] tracking-[-0.015em] text-[clamp(30px,3.4vw,52px)] text-foreground"
          style={{ textWrap: "balance" }}
        >
          {line1}
          {line2 ? (
            <>
              <br />
              {line2}
            </>
          ) : null}
        </h2>
        {body ? (
          <p className="mx-auto mt-8 max-w-[62ch] text-[15px] leading-[1.75] text-foreground/70">
            {body}
          </p>
        ) : null}
        {cta && cfg.ctaHref ? (
          <Link
            to={cfg.ctaHref || ""}
            className="mt-10 inline-flex min-h-[48px] items-center justify-center bg-foreground px-9 text-[11px] uppercase tracking-[0.22em] text-background transition-opacity hover:opacity-85"
          >
            {cta}
          </Link>
        ) : null}
      </div>
    </section>
  );
}

/* ───────────────────── بلوك المنتج نصفين + شريط صور ───────────────────── */

function Spotlight({
  options,
  upgrade,
  cfg,
}: {
  /** المنتج المميَّز أوّلاً ثمّ البدائل — الزائر يبدّل فتتبدّل الصور والتعريف. */
  options: ShopifyProduct[];
  upgrade?: ShopifyProduct;
  cfg: HomeDesktopConfig["spotlight"];
}) {
  const { t, locale, formatPrice } = useLocale();
  const ar = locale === "ar";
  const translations = useProductTranslations();

  const [activeHandle, setActiveHandle] = React.useState(
    options[0]?.node.handle ?? "",
  );
  const active =
    options.find((p) => p.node.handle === activeHandle) ?? options[0]!;
  const node = active.node;

  const title = localizedTitle(node, locale, translations);
  const desc = localizedDescription(node, locale, translations);
  const price = node.priceRange.minVariantPrice;
  const images = node.images.edges.map((e) => e.node);
  const main = images[0];
  /** صور العطور داخل المجموعة — تحلّ محلّ نصوص «ما في الداخل». */
  const insideImages = images.slice(1, 6);

  const ctaLabel =
    (ar ? cfg.ctaLabelAr : cfg.ctaLabelEn).trim() || t("home.d.spotlight.cta");

  const upNode = cfg.showUpgrade ? upgrade?.node : undefined;
  const upTitle = upNode ? localizedTitle(upNode, locale, translations) : "";

  return (
    <section className="hidden border-y border-foreground bg-background md:block">
      <div className="grid grid-cols-1 md:grid-cols-2">
        {/* وسائط المنتج */}
        <div className="relative min-h-[520px] overflow-hidden bg-secondary/40 lg:min-h-[680px]">
          {cfg.mediaUrl ? (
            <AutoMedia
              src={cfg.mediaUrl}
              alt={title}
              className="absolute inset-0 h-full w-full object-cover"
            />
          ) : main ? (
            <img
              src={shopifyImg(main.url, 1600)}
              srcSet={shopifySrcSet(main.url, [800, 1200, 1600, 2000])}
              sizes="50vw"
              alt={main.altText || title}
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover"
            />
          ) : null}
        </div>

        {/* تفاصيل المنتج */}
        <div className="flex flex-col justify-center border-s border-foreground/10 px-[clamp(32px,4vw,72px)] py-[clamp(48px,5vw,80px)]">
          <h2 className="font-serif font-normal leading-[1.12] tracking-[-0.015em] text-[clamp(26px,2.8vw,44px)] text-foreground">
            {title}
          </h2>
          <p className="mt-3 text-[15px] text-foreground/70">
            {formatPrice(price.amount, price.currencyCode)}
          </p>

          {desc ? (
            <p className="mt-4 max-w-[46ch] text-[13.5px] leading-relaxed text-foreground/70">
              {desc.length > 240 ? `${desc.slice(0, 238)}…` : desc}
            </p>
          ) : null}

          {insideImages.length > 0 ? (
            <>
              <p className="mt-8 text-[11px] uppercase tracking-[0.2em] text-foreground/60">
                {t("home.d.spotlight.inside")}
              </p>
              <div className="mt-3 grid grid-cols-1 gap-px overflow-hidden border border-foreground/15 bg-foreground/15 sm:grid-cols-2">
                {insideImages.map((img, i) => {
                  const raw = (img.altText || "").trim();
                  const [namePart, detailPart] = raw.includes("·")
                    ? [
                        raw.slice(0, raw.indexOf("·")).trim(),
                        raw.slice(raw.indexOf("·") + 1).trim(),
                      ]
                    : [raw, ""];
                  const name = namePart || `${title} · ${i + 1}`;
                  const isLastOdd =
                    i === insideImages.length - 1 &&
                    insideImages.length % 2 === 1;
                  return (
                    <div
                      key={i}
                      className={`flex items-center gap-4 bg-background p-4 ${
                        isLastOdd ? "sm:col-span-2" : ""
                      }`}
                    >

                      <div className="relative h-14 w-14 shrink-0 overflow-hidden bg-background">
                        <img
                          src={shopifyImg(img.url, 320)}
                          alt={name}
                          loading="lazy"
                          className="absolute inset-0 h-full w-full object-contain"
                        />
                      </div>
                      <div className="min-w-0">
                        <p className="truncate text-[11px] uppercase tracking-[0.16em] text-foreground">
                          {name}
                        </p>
                        {detailPart ? (
                          <p className="mt-1 truncate text-[11.5px] text-foreground/60">
                            {detailPart}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          ) : null}


          {options.length > 1 ? (
            <div className="mt-9">
              <p className="text-[11px] uppercase tracking-[0.2em] text-foreground/60">
                {t("home.d.spotlight.choose")}
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {options.map((p) => {
                  const isActive = p.node.handle === node.handle;
                  return (
                    <button
                      key={p.node.id}
                      type="button"
                      onClick={() => setActiveHandle(p.node.handle)}
                      aria-pressed={isActive}
                      className={`min-h-[40px] border px-4 text-[11px] uppercase tracking-[0.16em] transition-colors ${
                        isActive
                          ? "border-foreground bg-foreground text-background"
                          : "border-foreground/25 text-foreground/75 hover:border-foreground/60 hover:text-foreground"
                      }`}
                    >
                      {localizedTitle(p.node, locale, translations)}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}

          <Link
            to="/product/$handle"
            params={{ handle: node.handle }}
            className="mt-9 inline-flex min-h-[52px] items-center justify-center bg-foreground px-8 text-[11px] uppercase tracking-[0.22em] text-background transition-opacity hover:opacity-85"
          >
            {ctaLabel}
          </Link>

          {upNode ? (
            <div className="mt-10">
              <p className="text-[11px] uppercase tracking-[0.2em] text-foreground/60">
                {t("home.d.spotlight.upgrade")}
              </p>
              <div className="mt-3 flex items-center gap-4 border border-foreground/15 p-4">
                {upNode.images.edges[0] ? (
                  <img
                    src={shopifyImg(upNode.images.edges[0].node.url, 320)}
                    alt={upTitle}
                    loading="lazy"
                    className="h-16 w-16 object-cover"
                  />
                ) : null}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13px] text-foreground">{upTitle}</p>
                  <p className="mt-1 text-[12.5px] text-foreground/60">
                    {formatPrice(
                      upNode.priceRange.minVariantPrice.amount,
                      upNode.priceRange.minVariantPrice.currencyCode,
                    )}
                  </p>
                </div>
                <Link
                  to="/product/$handle"
                  params={{ handle: upNode.handle }}
                  className="inline-flex min-h-[44px] items-center bg-foreground px-5 text-[11px] uppercase tracking-[0.2em] text-background transition-opacity hover:opacity-85"
                >
                  {t("home.d.spotlight.add")}
                </Link>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────── شبكة العطور ─────────────────────────── */

function FragrancesGrid({
  products,
  cfg,
}: {
  products: ShopifyProduct[];
  cfg: HomeDesktopConfig["fragrances"];
}) {
  const { t, locale, formatPrice } = useLocale();
  const ar = locale === "ar";
  const translations = useProductTranslations();
  if (products.length === 0) return null;

  const heading =
    (ar ? cfg.titleAr : cfg.titleEn).trim() || t("home.d.fragrances.title");

  return (
    <section className="hidden bg-background md:block">
      <div className="flex items-center justify-between px-10 py-[clamp(40px,4vw,68px)] lg:px-16">
        <span aria-hidden className="hidden w-24 lg:block" />
        <h2 className="font-serif text-[clamp(18px,1.6vw,26px)] font-normal text-foreground">
          {heading}
        </h2>
        <Link
          to="/shop"
          className="inline-flex min-h-[36px] items-center border-b border-foreground/40 pb-0.5 text-[11px] uppercase tracking-[0.2em] text-foreground/80 transition-colors hover:text-foreground"
        >
          {t("home.d.fragrances.all")}
        </Link>
      </div>

      <div className="grid grid-cols-4 gap-px border-y border-foreground/12 bg-foreground/12">
        {products.slice(0, 8).map(({ node }) => {
          const title = localizedTitle(node, locale, translations);
          const img = node.images.edges[0]?.node;
          const sizes = node.options
            .find((o) => /size|volume|حجم/i.test(o.name))
            ?.values.slice(0, 3);
          return (
            <Link
              key={node.id}
              to="/product/$handle"
              params={{ handle: node.handle }}
              className="group bg-background"
            >
              <div className="relative aspect-[3/4] overflow-hidden bg-secondary/40">
                {img ? (
                  <img
                    src={shopifyImg(img.url, 1000)}
                    srcSet={shopifySrcSet(img.url, [600, 900, 1200, 1600])}
                    sizes="(min-width: 1536px) 25vw, 30vw"
                    alt={img.altText || title}
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-[1.05]"
                  />
                ) : null}
              </div>
              <div className="px-5 py-5">
                <h3 className="text-[11.5px] uppercase tracking-[0.16em] text-foreground">
                  {title}
                </h3>
                <p className="mt-2 text-[12.5px] text-foreground/70">
                  {formatPrice(
                    node.priceRange.minVariantPrice.amount,
                    node.priceRange.minVariantPrice.currencyCode,
                  )}
                </p>
                {sizes?.length ? (
                  <p className="mt-1 text-[11.5px] text-foreground/50">
                    {sizes.join(" · ")}
                  </p>
                ) : null}
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}

/* ─────────────────────────── التخطيط الكامل ─────────────────────────── */

export function HomeDesktopEditorial() {
  const { countryCode } = useLocale();
  const isMobile = useIsMobile();
  const [desktopReady, setDesktopReady] = React.useState(false);
  React.useEffect(() => {
    setDesktopReady(window.matchMedia("(min-width: 768px)").matches);
  }, []);
  const config = useHomeDesktopConfig(desktopReady);
  const { data } = useQuery({
    queryKey: ["home-desktop-products", countryCode],
    queryFn: () => fetchProducts(20, countryCode),
    staleTime: 5 * 60_000,
    enabled: desktopReady,
  });
  const products = React.useMemo(() => data ?? [], [data]);

  const byHandle = React.useCallback(
    (handle: string) =>
      handle ? products.find((p) => p.node.handle === handle) : undefined,
    [products],
  );

  /** بلا اختيار أدمن: نُفضّل منتجات مجموعة الهدايا (collection/gift/set) لا أوّل منتج. */
  const giftSetFallback = React.useMemo(
    () =>
      products.find((p) =>
        /collection|gift|set/i.test(
          `${p.node.handle} ${p.node.title} ${p.node.productType ?? ""}`,
        ),
      ),
    [products],
  );
  const hero =
    byHandle(config.spotlight.productHandle) ?? giftSetFallback ?? products[0];
  /** بدائل تلقائيّة من بقيّة مجموعات الهدايا عند غياب اختيار الأدمن. */
  const autoAlts = React.useMemo(
    () =>
      products.filter(
        (p) =>
          /collection|gift|set/i.test(
            `${p.node.handle} ${p.node.title} ${p.node.productType ?? ""}`,
          ) && p.node.handle !== hero?.node.handle,
      ),
    [products, hero],
  );

  const spotlightOptions = React.useMemo(() => {
    if (!hero) return [];
    const list = [hero];
    const alts = config.spotlight.altHandles.length
      ? config.spotlight.altHandles
          .map((h) => byHandle(h))
          .filter((p): p is ShopifyProduct => Boolean(p))
      : autoAlts.slice(0, 5);
    for (const p of alts) {
      if (!list.some((x) => x.node.handle === p.node.handle)) list.push(p);
    }
    return list;
  }, [hero, config.spotlight.altHandles, byHandle, autoAlts]);

  const upgrade =
    byHandle(config.spotlight.upgradeHandle) ??
    products.find((p) => p.node.handle !== hero?.node.handle);

  const picked = config.fragrances.handles
    .map((h) => byHandle(h))
    .filter((p): p is ShopifyProduct => Boolean(p));
  const gridProducts =
    picked.length > 0
      ? picked
      : products.filter((p) => p.node.handle !== hero?.node.handle).slice(0, 4);

  if (isMobile || !desktopReady) return null;

  return (
    <div className="hidden md:block">
      {config.statement.enabled ? <Statement cfg={config.statement} /> : null}
      {config.spotlight.enabled && spotlightOptions.length > 0 ? (
        <Spotlight
          options={spotlightOptions}
          upgrade={upgrade}
          cfg={config.spotlight}
        />
      ) : null}
      {config.fragrances.enabled ? (
        <FragrancesGrid products={gridProducts} cfg={config.fragrances} />
      ) : null}
      <OutroVideo cfg={config.outro} />
    </div>
  );
}
